import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Offer/campaign/creative dates are entered and displayed in this fixed
// business timezone, then stored as UTC — so times don't shift with the
// viewer's browser location.
export const DISPLAY_TIME_ZONE = 'America/Toronto';

// Stored UTC value (ISO string / Date) -> `YYYY-MM-DD` calendar day in the
// fixed business zone, for a native `<input type="date">`.
export function toZonedDateInput(
  value: string | number | Date | null | undefined
): string {
  if (!value) return '';
  return formatInTimeZone(value, DISPLAY_TIME_ZONE, 'yyyy-MM-dd');
}

// `YYYY-MM-DD` picked in the fixed business zone -> UTC ISO at the start of
// that day, for storage.
export function fromZonedDateInput(
  day: string | null | undefined
): string | null {
  if (!day) return null;
  return fromZonedTime(`${day}T00:00:00`, DISPLAY_TIME_ZONE).toISOString();
}

const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

export function formatLanguageName(code: string): string {
  return languageNames.of(code) ?? code;
}

export function formatCountryName(code: string): string {
  return countryNames.of(code) ?? code;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(
  isoString: string | null | undefined,
  style: 'long' | 'short' = 'long'
): string {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: style,
    day: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  });
}
